import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Unhandled render error", error, errorInfo.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center text-foreground"
      >
        <h1 className="text-base font-semibold">Something went wrong</h1>
        <p className="max-w-md text-xs text-muted-foreground">
          TrackInsiders hit an unexpected error and could not render this view. Reloading usually
          fixes it; if it keeps happening, please try again later.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="border border-border bg-surface-elevated px-3 py-1.5 text-xs font-medium hover:bg-surface-inset"
        >
          Reload page
        </button>
      </div>
    );
  }
}
